import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Inbox,
  User,
  Users,
  UserMinus,
  UserPlus,
  UserCheck,
} from "lucide-react";
import API from "../../services/api";
import { useToast } from "../Context/ToastContext";
import { useEmployees } from "../../hooks/useEmployees";
import {
  applyDirectoryFilters,
  applyJoiningFilters,
  applyMasterFilters,
  buildExitReportRows,
  computeWorkforceKpis,
  directoryReportRows,
  employeeFullName,
  exitReportPreviewRows,
  exportRowsToXlsx,
  fetchAllEmployees,
  joiningReportPreviewRows,
  joiningReportRows,
  masterReportRows,
  monthBounds,
  priorMonthBounds,
  toIsoDate,
} from "./employeeReportsUtils";
import ReportPageHeader from "./components/ReportPageHeader";
import ReportLibraryHeader from "./components/ReportLibraryHeader";
import ReportCard from "./components/ReportCard";
import ReportActions from "./components/ReportActions";
import ReportViewModal from "./components/ReportViewModal";
import ReportFilterField from "./components/ReportFilterField";
import "../../styles/employeeReports.css";
import "../../styles/reportsEnterprise.css";

const PREVIEW_LIMIT = 5;

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const DATE_RANGE_OPTIONS = [
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom" },
];

const MASTER_PREVIEW_COLS = [
  "Employee Code",
  "Employee Name",
  "Department",
  "Designation",
  "DOJ",
  "Status",
];

const DIRECTORY_PREVIEW_COLS = [
  "Employee Name",
  "Mobile",
  "Email",
  "Department",
  "Designation",
];

const JOINING_PREVIEW_COLS = [
  "Employee Name",
  "Department",
  "Designation",
  "Joining Date",
];

const EXIT_PREVIEW_COLS = [
  "Employee Name",
  "Department",
  "Exit Date",
  "Exit Reason",
  "Status",
];

function useReportPreview(loader, deps) {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loader()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setRows(list.slice(0, PREVIEW_LIMIT));
        setTotalCount(list.length);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setTotalCount(0);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { rows, loading, totalCount };
}

function KpiCard({ label, value, hint, icon: Icon, tone, loading }) {
  return (
    <article className={`er-kpi-card er-kpi-card--${tone}`}>
      <div>
        <p className="er-kpi-label">{label}</p>
        {loading ? (
          <div className="er-kpi-skeleton" aria-hidden="true" />
        ) : (
          <p className="er-kpi-value">{value}</p>
        )}
        {hint && <p className="er-kpi-hint">{hint}</p>}
      </div>
      <div className={`er-kpi-icon er-kpi-icon--${tone}`}>
        <Icon size={20} />
      </div>
    </article>
  );
}

function PreviewPanel({ columns, rows, loading, totalCount }) {
  return (
    <div className="er-preview">
      <div className="er-preview-head">
        <p className="er-preview-label">Preview</p>
        {!loading && (
          <span className="er-preview-count">
            {totalCount === 0 ? "0 records" : `Showing ${Math.min(PREVIEW_LIMIT, totalCount)} of ${totalCount}`}
          </span>
        )}
      </div>
      {loading ? (
        <div className="er-preview-loading">
          <div className="er-spinner" aria-hidden="true" />
          Loading preview…
        </div>
      ) : !rows.length ? (
        <div className="er-preview-empty">
          <div className="er-preview-empty-icon">
            <Inbox size={16} />
          </div>
          No Records Found
        </div>
      ) : (
        <div className="er-preview-table-wrap">
          <table className="er-preview-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  {columns.map((col) => (
                    <td key={col}>{row[col] ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function EmployeeReportsPage() {
  const toast = useToast();
  const { employees: employeeOptions } = useEmployees();
  const monthDefault = useMemo(() => monthBounds(), []);
  const priorDefault = useMemo(() => priorMonthBounds(), []);

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [busy, setBusy] = useState("");
  const [viewModal, setViewModal] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);

  const [masterFilters, setMasterFilters] = useState({
    employeeId: "",
    departmentId: "",
    designationId: "",
    status: "",
  });

  const [directoryFilters, setDirectoryFilters] = useState({
    departmentId: "",
    designationId: "",
  });

  const [joiningFilters, setJoiningFilters] = useState({
    fromDate: monthDefault.from,
    toDate: monthDefault.to,
    departmentId: "",
  });

  const [exitFilters, setExitFilters] = useState({
    dateRange: "custom",
    fromDate: priorDefault.from,
    toDate: priorDefault.to,
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    Promise.all([
      API.get("/setup/departments", { headers }),
      API.get("/setup/designations", { headers }),
    ])
      .then(([deptRes, desigRes]) => {
        setDepartments(deptRes.data || []);
        setDesignations(desigRes.data || []);
      })
      .catch(() => {
        setDepartments([]);
        setDesignations([]);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setKpiLoading(true);
    fetchAllEmployees({ activeOnly: false })
      .then((rows) => {
        if (!cancelled) {
          setKpis(computeWorkforceKpis(rows));
          setKpiLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setKpis({ total: 0, active: 0, inactive: 0, newJoiners: 0 });
          setKpiLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const resolveActiveOnly = (status) => {
    if (status === "active") return true;
    if (status === "inactive") return false;
    return false;
  };

  const loadMasterRows = useCallback(async () => {
    const rows = await fetchAllEmployees({
      departmentId: masterFilters.departmentId,
      designationId: masterFilters.designationId,
      activeOnly: resolveActiveOnly(masterFilters.status),
    });
    return masterReportRows(applyMasterFilters(rows, masterFilters));
  }, [masterFilters]);

  const loadDirectoryRows = useCallback(async () => {
    const rows = await fetchAllEmployees({ activeOnly: false });
    return directoryReportRows(applyDirectoryFilters(rows, directoryFilters));
  }, [directoryFilters]);

  const loadJoiningRows = useCallback(async () => {
    const rows = await fetchAllEmployees({
      departmentId: joiningFilters.departmentId,
      activeOnly: false,
    });
    return joiningReportRows(applyJoiningFilters(rows, joiningFilters));
  }, [joiningFilters]);

  const loadJoiningPreview = useCallback(async () => {
    const rows = await fetchAllEmployees({
      departmentId: joiningFilters.departmentId,
      activeOnly: false,
    });
    const filtered = applyJoiningFilters(rows, joiningFilters);
    return joiningReportPreviewRows(filtered);
  }, [joiningFilters]);

  const loadMasterPreview = useCallback(() => loadMasterRows(), [loadMasterRows]);

  const loadDirectoryPreview = useCallback(() => loadDirectoryRows(), [loadDirectoryRows]);

  const loadExitPreview = useCallback(async () => {
    const raw = await buildExitReportRows(exitFilters);
    return exitReportPreviewRows(raw);
  }, [exitFilters]);

  const loadExitRows = useCallback(async () => {
    const raw = await buildExitReportRows(exitFilters);
    return exitReportPreviewRows(raw);
  }, [exitFilters]);

  const masterPreview = useReportPreview(loadMasterPreview, [
    loadMasterPreview,
    masterFilters.employeeId,
    masterFilters.departmentId,
    masterFilters.designationId,
    masterFilters.status,
  ]);

  const directoryPreview = useReportPreview(loadDirectoryPreview, [
    loadDirectoryPreview,
    directoryFilters.departmentId,
    directoryFilters.designationId,
  ]);

  const joiningPreview = useReportPreview(loadJoiningPreview, [
    loadJoiningPreview,
    joiningFilters.fromDate,
    joiningFilters.toDate,
    joiningFilters.departmentId,
  ]);

  const exitPreview = useReportPreview(loadExitPreview, [
    loadExitPreview,
    exitFilters.fromDate,
    exitFilters.toDate,
    exitFilters.dateRange,
  ]);

  const handlePdf = () => {
    toast.error("PDF export is not available yet. Backend PDF generation requires approval.");
  };

  const runAction = async (key, loader, filename, sheetName, viewTitle, viewColumns) => {
    try {
      setBusy(key);
      const rows = await loader();
      if (key.endsWith("view")) {
        const columns = viewColumns || (rows.length ? Object.keys(rows[0]) : []);
        setViewModal({ title: viewTitle, columns, rows });
        return;
      }
      exportRowsToXlsx(rows, sheetName, filename);
      toast.success("Excel export downloaded.");
    } catch {
      toast.error("Failed to generate report. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const applyExitDateRange = (value) => {
    if (value === "this_month") {
      const b = monthBounds();
      setExitFilters({ dateRange: value, fromDate: b.from, toDate: b.to });
      return;
    }
    if (value === "last_month") {
      const b = priorMonthBounds();
      setExitFilters({ dateRange: value, fromDate: b.from, toDate: b.to });
      return;
    }
    setExitFilters((prev) => ({ ...prev, dateRange: "custom" }));
  };

  const monthLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleString("en-US", { month: "short", year: "numeric" });
  }, []);

  return (
    <div className="rpt-page er-page rpt-page--enterprise">
      <ReportPageHeader
        icon={Users}
        category="Reports · Employee"
        title="Employee Reports"
        subtitle="Employee master and workforce-related information."
        breadcrumbCurrent="Employee Reports"
      />

      <section className="er-kpi-grid" aria-label="Workforce summary">
        <KpiCard
          label="Total Employees"
          value={kpis?.total ?? 0}
          hint="All workforce records"
          icon={Users}
          tone="blue"
          loading={kpiLoading}
        />
        <KpiCard
          label="Active Employees"
          value={kpis?.active ?? 0}
          hint="Currently active"
          icon={UserCheck}
          tone="green"
          loading={kpiLoading}
        />
        <KpiCard
          label="New Joiners"
          value={kpis?.newJoiners ?? 0}
          hint={`This month · ${monthLabel}`}
          icon={UserPlus}
          tone="violet"
          loading={kpiLoading}
        />
        <KpiCard
          label="Inactive Employees"
          value={kpis?.inactive ?? 0}
          hint="Inactive or exited"
          icon={UserMinus}
          tone="amber"
          loading={kpiLoading}
        />
      </section>

      <ReportLibraryHeader
        variant="enterprise"
        subtitle="Filter, preview, and export workforce reports"
        hint="Each report below is a full-width workspace. Set filters, preview data, then View / Excel / PDF."
      />

      <div className="rpt-report-list">
        <ReportCard
          layout="enterprise"
          reportType="employee-master"
          className="er-card--fullpage"
          icon={User}
          iconTone="blue"
          title="Employee Master Report"
          description="Complete list of all employees with org details."
          filters={
            <>
              <ReportFilterField id="er-master-employee" label="Employee">
                <select
                  id="er-master-employee"
                  value={masterFilters.employeeId}
                  onChange={(e) => setMasterFilters((f) => ({ ...f, employeeId: e.target.value }))}
                >
                  <option value="">All Employees</option>
                  {employeeOptions.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {employeeFullName(emp)}
                      {emp.employee_code ? ` (${emp.employee_code})` : ""}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
              <ReportFilterField id="er-master-dept" label="Department">
                <select
                  id="er-master-dept"
                  value={masterFilters.departmentId}
                  onChange={(e) => setMasterFilters((f) => ({ ...f, departmentId: e.target.value }))}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
              <ReportFilterField id="er-master-desig" label="Designation">
                <select
                  id="er-master-desig"
                  value={masterFilters.designationId}
                  onChange={(e) => setMasterFilters((f) => ({ ...f, designationId: e.target.value }))}
                >
                  <option value="">All Designations</option>
                  {designations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
              <ReportFilterField id="er-master-status" label="Status">
                <select
                  id="er-master-status"
                  value={masterFilters.status}
                  onChange={(e) => setMasterFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              onView={() =>
                runAction("master-view", loadMasterRows, "", "", "Employee Master Report", MASTER_PREVIEW_COLS)
              }
              onExcel={() =>
                runAction("master-excel", loadMasterRows, "employee_master_report.xlsx", "Employee Master", "")
              }
              onPdf={handlePdf}
            />
          }
          footer={
            <PreviewPanel
              columns={MASTER_PREVIEW_COLS}
              rows={masterPreview.rows}
              loading={masterPreview.loading}
              totalCount={masterPreview.totalCount}
            />
          }
        />

        <ReportCard
          layout="enterprise"
          reportType="employee-directory"
          className="er-card--fullpage"
          icon={Users}
          iconTone="green"
          title="Employee Directory"
          description="Employee contact and department details."
          filters={
            <>
              <ReportFilterField id="er-dir-dept" label="Department">
                <select
                  id="er-dir-dept"
                  value={directoryFilters.departmentId}
                  onChange={(e) => setDirectoryFilters((f) => ({ ...f, departmentId: e.target.value }))}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
              <ReportFilterField id="er-dir-desig" label="Designation">
                <select
                  id="er-dir-desig"
                  value={directoryFilters.designationId}
                  onChange={(e) => setDirectoryFilters((f) => ({ ...f, designationId: e.target.value }))}
                >
                  <option value="">All Designations</option>
                  {designations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              showPdf={false}
              onView={() =>
                runAction("directory-view", loadDirectoryRows, "", "", "Employee Directory", DIRECTORY_PREVIEW_COLS)
              }
              onExcel={() =>
                runAction("directory-excel", loadDirectoryRows, "employee_directory.xlsx", "Employee Directory", "")
              }
            />
          }
          footer={
            <PreviewPanel
              columns={DIRECTORY_PREVIEW_COLS}
              rows={directoryPreview.rows}
              loading={directoryPreview.loading}
              totalCount={directoryPreview.totalCount}
            />
          }
        />

        <ReportCard
          layout="enterprise"
          reportType="joining"
          className="er-card--fullpage"
          icon={CalendarDays}
          iconTone="purple"
          title="Joining Report"
          description="Employees joined during the selected period."
          filters={
            <>
              <ReportFilterField id="er-join-from" label="From Date">
                <input
                  id="er-join-from"
                  type="date"
                  value={joiningFilters.fromDate}
                  onChange={(e) =>
                    setJoiningFilters((f) => ({ ...f, fromDate: e.target.value || toIsoDate(new Date()) }))
                  }
                />
              </ReportFilterField>
              <ReportFilterField id="er-join-to" label="To Date">
                <input
                  id="er-join-to"
                  type="date"
                  value={joiningFilters.toDate}
                  onChange={(e) =>
                    setJoiningFilters((f) => ({ ...f, toDate: e.target.value || toIsoDate(new Date()) }))
                  }
                />
              </ReportFilterField>
              <ReportFilterField id="er-join-dept" label="Department">
                <select
                  id="er-join-dept"
                  value={joiningFilters.departmentId}
                  onChange={(e) => setJoiningFilters((f) => ({ ...f, departmentId: e.target.value }))}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              onView={() =>
                runAction("joining-view", loadJoiningRows, "", "", "Joining Report", JOINING_PREVIEW_COLS)
              }
              onExcel={() =>
                runAction("joining-excel", loadJoiningRows, "joining_report.xlsx", "Joining Report", "")
              }
              onPdf={handlePdf}
            />
          }
          footer={
            <PreviewPanel
              columns={JOINING_PREVIEW_COLS}
              rows={joiningPreview.rows}
              loading={joiningPreview.loading}
              totalCount={joiningPreview.totalCount}
            />
          }
        />

        <ReportCard
          layout="enterprise"
          reportType="exit"
          className="er-card--fullpage"
          icon={UserMinus}
          iconTone="orange"
          title="Exit / Inactive Employee Report"
          description="Resigned and inactive employees in the selected period."
          filters={
            <>
              <ReportFilterField id="er-exit-range" label="Date Range">
                <select
                  id="er-exit-range"
                  value={exitFilters.dateRange}
                  onChange={(e) => applyExitDateRange(e.target.value)}
                >
                  {DATE_RANGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
              <ReportFilterField id="er-exit-from" label="From Date">
                <input
                  id="er-exit-from"
                  type="date"
                  value={exitFilters.fromDate}
                  onChange={(e) =>
                    setExitFilters((f) => ({
                      ...f,
                      dateRange: "custom",
                      fromDate: e.target.value || f.fromDate,
                    }))
                  }
                />
              </ReportFilterField>
              <ReportFilterField id="er-exit-to" label="To Date">
                <input
                  id="er-exit-to"
                  type="date"
                  value={exitFilters.toDate}
                  onChange={(e) =>
                    setExitFilters((f) => ({
                      ...f,
                      dateRange: "custom",
                      toDate: e.target.value || f.toDate,
                    }))
                  }
                />
              </ReportFilterField>
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              onView={() =>
                runAction("exit-view", loadExitRows, "", "", "Exit / Inactive Employee Report", EXIT_PREVIEW_COLS)
              }
              onExcel={() =>
                runAction("exit-excel", loadExitRows, "exit_inactive_report.xlsx", "Exit Report", "")
              }
              onPdf={handlePdf}
            />
          }
          footer={
            <PreviewPanel
              columns={EXIT_PREVIEW_COLS}
              rows={exitPreview.rows}
              loading={exitPreview.loading}
              totalCount={exitPreview.totalCount}
            />
          }
        />
      </div>

      {viewModal && (
        <ReportViewModal
          title={viewModal.title}
          columns={viewModal.columns}
          rows={viewModal.rows}
          modalId="er-modal-title"
          onClose={() => setViewModal(null)}
        />
      )}
    </div>
  );
}
